import { Component, type ErrorInfo, type ReactNode } from "react";
import { AppLogger } from "@utils/index";

/**
 * Props interface following TypeScript naming convention
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  title?: string;
  message?: string;
  buttonLabel?: string;
}

/**
 * State interface following TypeScript naming convention
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary component for catching React errors
 * Note: Error boundaries must be class components
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        AppLogger.error("Uncaught error:", error, errorInfo);
    }

    handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        if (this.state.hasError) {
            return this.props.fallback ?? (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center p-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-white">
                            {this.props.title ?? "Something went wrong"}
                        </h1>
                        <p className="text-gray-600 mb-6 dark:text-gray-400">
                            {this.props.message ?? "An unexpected error occurred. Please try reloading the page."}
                        </p>
                        {this.state.error && (
                            <pre className="text-left bg-gray-100 p-4 rounded mb-6 text-sm overflow-auto max-w-lg mx-auto dark:bg-gray-800 dark:text-gray-300">
                                {this.state.error.message}
                            </pre>
                        )}
                        <button
                            onClick={this.handleReload}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            {this.props.buttonLabel ?? "Reload Page"}
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
